import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { ApiEnvelope } from '@/types/package';
import { HandoverChecklistItemInput } from '@/services/api/handovers';

// Dash Rent ops surface. Handover creation is documented here rather than on
// the mgmt surface; per TRD O6 it authenticates the same way FMS web does
// (Google sign-in + access whitelist), so FMS can call it directly.
const BASE = '/rent/ops/v1';

const unwrap = <T>(payload: ApiEnvelope<T>): T => payload?.data as T;

export interface OpsHandoverListItem {
  /**
   * What the scan and the submit below are keyed on. Null only on a row with no
   * handover yet; `rentalID` rides along for display and linking.
   */
  handoverID: string | null;
  rentalID: string;
  slot: { date: string; startTime: string; endTime?: string } | null;
  driver: { id: number; name: string; code: string };
  package: { id: string; name: string; contractType: string };
  vehicle: { id: string; platNomor: string; modelName: string } | null;
  dpPaid: boolean;
}

/**
 * Both parties sign on one device (TRD O8). Bare base64 PNG, no `data:` prefix
 * — the same shape `fileToBase64` produces for every other upload here.
 */
export interface HandoverSignatures {
  driver: string;
  ops: string;
}

export interface CreateHandoverBody {
  /**
   * The HANDOVER, not the rental: possession lives on the handover row, and the
   * endpoint moves that row WAITING_HANDOVER -> ACTIVE. It used to take
   * `rentalID`, which a body sending it now fails validation on ("handoverID
   * must be a string") rather than falling back.
   */
  handoverID: string;
  /** Must be an integer — sent as a string it is rejected, not coerced (B6). */
  odometer: number;
  /**
   * Per-part checklist (present/absent + per-item photo). Source of truth; the
   * server derives equipment labels + first-photo thumbnail from it. Required —
   * a body without at least one item is a 400.
   */
  checklistItems: HandoverChecklistItemInput[];
  /**
   * Required, not optional. The DTO has always demanded it, and since B6 it is
   * validated *and* persisted — a body without it is a 400, where it used to be
   * accepted and silently dropped.
   */
  signatures: HandoverSignatures;
  notes?: string;
}

export interface CreateHandoverResponse {
  handoverID: string;
  rentalStatus: string;
  bastUrl?: string | null;
  /** Set when the BAST PDF is still being generated (D3 upload fallback). */
  bastPending?: boolean;
}

/** Today's bookings whose HANDOVER is WAITING_HANDOVER, at the hub. */
export const getOpsHandovers = async (
  params: { date?: string } = {}
): Promise<OpsHandoverListItem[]> => {
  try {
    const response = await Axios.get<ApiEnvelope<OpsHandoverListItem[]>>(
      `${BASE}/handovers`,
      { params }
    );
    return unwrap(response.data) ?? [];
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** QR prefill — verifies the person, not just the bike (TRD O7). */
export const scanHandover = async (
  qrPayload: string
): Promise<Record<string, unknown>> => {
  try {
    const response = await Axios.post<ApiEnvelope<Record<string, unknown>>>(
      `${BASE}/handovers/scan`,
      { qrPayload }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Completes the physical handover, moving possession to ACTIVE. The
 * backend hard-gates on the DP being PAID (TRD B3).
 */
export const createHandover = async (
  body: CreateHandoverBody
): Promise<CreateHandoverResponse> => {
  try {
    const response = await Axios.post<ApiEnvelope<CreateHandoverResponse>>(
      `${BASE}/handovers`,
      body
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Only what FMS reads off the ops handover detail so far. */
export interface OpsHandoverDetail {
  ID: string;
  /** Null on rows recorded before the per-part checklist existed. */
  handoverChecklist?: RecordedChecklistItem[] | null;
}

export const getHandoverById = async (
  id: string
): Promise<OpsHandoverDetail> => {
  try {
    const response = await Axios.get<ApiEnvelope<OpsHandoverDetail>>(
      `${BASE}/handovers/${id}`
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** One part of a recorded checklist, as the handover row stores it. */
export interface RecordedChecklistItem {
  code: string;
  label: string;
  category: 'EQUIPMENT' | 'CONDITION';
  present: boolean;
  photoUrl: string | null;
  /**
   * False when the server defaulted the part to absent because the app never
   * asked about it — "not assessed", not "checked and missing". Absent on
   * rows stored before the flag existed, which count as captured.
   */
  captured?: boolean;
}

/** One Ada / Tidak ada answer a correction flipped. */
export interface ChecklistCorrectionChange {
  code: string;
  label: string;
  /** The answer the correction set; the previous one is its negation. */
  present: boolean;
}

export interface ChecklistCorrection {
  ID: string;
  changes: ChecklistCorrectionChange[];
  reason: string;
  createdBy: string;
  createdAt: string;
}

export interface CorrectHandoverChecklistBody {
  /** Only the flipped answers need sending; unchanged ones are ignored. */
  checklistItems: HandoverChecklistItemInput[];
  /** Printed on the reissued documents beside each corrected row. */
  reason: string;
}

/** A document the correction queued for reissue. */
export type CorrectedDocument = 'BAST' | 'HANDOVER' | 'RETURN';

export interface CorrectHandoverChecklistResponse {
  handoverID: string;
  correction: ChecklistCorrection;
  handoverChecklist: RecordedChecklistItem[];
  /** Rendered in the background, so each lands a moment after this reply. */
  reissuing: CorrectedDocument[];
}

/** Earlier corrections of the pickup checklist, oldest first. */
export const getHandoverChecklistCorrections = async (
  handoverID: string
): Promise<ChecklistCorrection[]> => {
  try {
    const response = await Axios.get<ApiEnvelope<ChecklistCorrection[]>>(
      `${BASE}/handovers/${handoverID}/checklist-corrections`
    );
    return unwrap(response.data) ?? [];
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Fixes answers ops got wrong at pickup, then reissues the BAST and the
 * Perjanjian Sewa Kontrak copies that print the checklist.
 */
export const correctHandoverChecklist = async (
  handoverID: string,
  body: CorrectHandoverChecklistBody
): Promise<CorrectHandoverChecklistResponse> => {
  try {
    const response = await Axios.post<
      ApiEnvelope<CorrectHandoverChecklistResponse>
    >(`${BASE}/handovers/${handoverID}/checklist-corrections`, body);
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
